import { store } from "../store";
import moment from "moment";
import feederListActions from "../_actions/feederListActions";
import mainTabActions from "../_actions/mainTabActions";
import adrActions from "../_actions/adrActions";
import { DOCTYPE } from "../_constants/docTypeConstants";
import { ADR, ADR_REVIEW, APP_WARNING, ERROR } from "../_constants/actionConstants";
import issueActions from "../_actions/issueActions";
import projActions from "../_actions/projActions";
import { createReport } from "../_actions/reportsActions";
import { pdfDocSections } from "../_constants/docSections";
import renderAdrHeaderV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrHeaderV3";
import renderAdrWpHoursV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrWpHoursV3";
import renderAdrGenCommentsV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrGenCommentsV3";
import renderAdrFooterV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrFooterV3";
import renderAdrAttachmentsV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrAttachmentsV3";
import renderAdrDescOfWorkV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrDescOfWorkV3";
import renderAdrSignatureV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrSignatureV3";
import renderAdrPageNoV3 from "../components/AdrV3/utils/adrPdfRenderFuncsV3/adrPageNoV3";
import adrPdfSectionDataV3 from "../components/AdrV3/utils/adrPdfSectionDataV3/adrPdfSectionDataV3";
import PdfDocument from "../_utils/pdfDocument";
import {
  convertNumberToSplitStation,
  convertSplitStationToString,
  fileExtensionIsImage,
  formatEaTitle,
} from "../_utils/generalUtils";
import { attachmentTypes, contextTypes } from "../_constants/fileUploadContextTypes";
import { getLocationName, getWpCaption } from "../components/AdrV3/utils/adrUtilsV3";
import renderAdrTaHeaderV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaHeaderV3";
import renderAdrTaLaborV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaLaborV3";
import renderAdrTaSignatureV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaSignatureV3";
import renderAdrTaFooterV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaFooterV3";
import renderAdrTaMaterialsV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaMaterialsV3";
import renderAdrTaEquipmentV3 from "../components/AdrV3/utils/adrTaRenderFuncsV3/adrTaEquipmentV3";
import adrTaPdfSectionDataV3 from "../components/AdrV3/utils/adrPdfTaSectionDataV3/adrTaPdfSectionDataV3";
import locationActions from "../_actions/locationActions";
import equipmentActions from "../_actions/equipmentActions";
import payEstimateActions from "../_actions/payEstimateActions";
import qSheetActions from "../_actions/qSheetActions";

/**
 * Scripts for performing groups of tasks.
 *
 * Notes on Implementation:
 * - Encapsulate as much of the process as possible within the script,
 *   including exception handling. (Handle all exceptions from promises
 *   here.) Ideally, client interaction should be kept to no more than:
 *   <code>actionScripts.OPEN_ADR.run(projectId)</code>
 * - Try to avoid returning Promises out of the script, unless the remaining
 *   process cannot be completed within the script. If a Promise must be returned,
 *   handle all exceptions from the encapsulated actions prior to return.
 * - When an exception occurs, roll back all actions that were initiated in
 *   the script, and log the error. If a tab was opened/registered, close or unregister.
 * - Do not create scripts for actions requiring only a single dispatch.
 *   Leave it to the client modules to handle the dispatch.
 */
export const actionScripts = {
  ADD_ERROR: {
    run: (newError) => {
      // Dispatch the rejection action (i.e. to set fetching = false)
      store.dispatch({ type: newError.type });
      const version = store.getState().appSettings.version;
      store.dispatch({
        type: ERROR.ADD_ERROR,
        newError: { ...newError, version },
      });
    },
  },
  ADD_WARNING: {
    run: (newWarning) => {
      store.dispatch({ type: newWarning.type });
      const version = store.getState().appSettings.version;
      store.dispatch({
        type: APP_WARNING.ADD_WARNING,
        newWarning: { ...newWarning, version },
      });
    },
  },
  OPEN_LINK: {
    run: (label) => {
      /** @type {MainTab} */
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.LINK,
        label,
      };
      store.dispatch(mainTabActions.addTab(tab));
    },
  },
  OPEN_ABOUT: {
    run: () => {
      /** @type {MainTab} */
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.ABOUT,
        label: "About",
        createdOn: Date.now(),
      };
      store.dispatch(mainTabActions.addTab(tab));
    },
  },
  OPEN_USER_PREFS: {
    run: () => {
      const { openTabs } = store.getState().mainTabs;
      // Force only 1 tab instance of Overtime
      const existUserPrefTab = openTabs.find(
        (t) => t.docType === DOCTYPE.USER_PREFERENCES
      );
      if (existUserPrefTab)
        store.dispatch(mainTabActions.selectTab(existUserPrefTab.tabId));
      else {
        /** @type {MainTab} */
        const tab = {
          idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
          docType: DOCTYPE.USER_PREFERENCES,
          label: "User Preferences",
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_PROJ_LIST: {
    run: (projectId) => {
      /** @type {MainTab} */
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.PROJECT_LIST,
        label: "Project List",
        projectId,
        metadata: { dist: 0 },
        createdOn: Date.now(),
      };
      store.dispatch(mainTabActions.addTab(tab));
    },
  },
  OPEN_OVERTIME: {
    run: () => {
      const { openTabs } = store.getState().mainTabs;
      // Force only 1 tab instance of Overtime
      const existOtTab = openTabs.find((t) => t.docType === DOCTYPE.OVERTIME_LIST);
      if (existOtTab) store.dispatch(mainTabActions.selectTab(existOtTab.tabId));
      else {
        /** @type {MainTab} */
        const tab = {
          idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
          docType: DOCTYPE.OVERTIME_LIST,
          label: "Overtime Log",
          createdOn: Date.now(),
        };
        // TODO: load all user overtimes here
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_REPORT_SELECTION: {
    run: () => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const tab = {
        idCheckoutToken,
        docType: DOCTYPE.REPORT_SELECTION,
        label: "Generate Reports",
      };
      store.dispatch(mainTabActions.addTab(tab));
    },
  },
  CREATE_REPORT: {
    run: async (reportParams) => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const [, jobStamp] = await Promise.all([
        store.dispatch(createReport(idCheckoutToken, reportParams)),
        store.dispatch(projActions.fetchJobStamp(reportParams.projectId)),
      ]);
      const newTab = {
        idCheckoutToken,
        docType: reportParams.reportType,
        projectId: reportParams.projectId,
        label: formatEaTitle(jobStamp, reportParams.label),
        createdOn: Date.now(),
      };
      store.dispatch(mainTabActions.addTab(newTab));
    },
  },
  OPEN_PROJ_ADMIN: {
    run: async (projectId) => {
      const { openTabs } = store.getState().mainTabs;

      // Force only 1 tab instance of ProjAdmin
      const existPaTab = openTabs.find(
        (t) => t.projectId === projectId && t.docType === DOCTYPE.PROJECT_SETUP
      );
      if (existPaTab) store.dispatch(mainTabActions.selectTab(existPaTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());

        // Initiate fetching of all static data (locationTypes, trades, etc.)
        store.dispatch(feederListActions.getAllStatic());
        const jobStamp = await store.dispatch(projActions.fetchJobStamp(projectId));

        // For loading project admin, validate genLocs first prior to
        // loading feederLists
        await store.dispatch(locationActions.validateGenericLocations(projectId));

        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.PROJECT_SETUP,
          label: (jobStamp ? jobStamp.fullEA : "(EA N/A)") + ": Project Admin",
          projectId,
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_PROJ_DASH: {
    run: async (projectId) => {
      const { openTabs } = store.getState().mainTabs;

      // Force only 1 tab instance of ProjDash
      const existPdTab = openTabs.find(
        (t) => t.projectId === projectId && t.docType === DOCTYPE.PROJECT_DASH
      );
      if (existPdTab) store.dispatch(mainTabActions.selectTab(existPdTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
        await store.dispatch(projActions.fetchDetails(projectId));
        const projDetails =
          store.getState().projects.openProjectDetails[projectId] || null;
        if (!projDetails) return;
        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.PROJECT_DASH,
          label: projDetails.fullEA + ": Project Dashboard",
          projectId,
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_PROJ_ASSIGNMENT: {
    run: (projectId = null) => {
      let label = "Project Assignment";
      if (projectId) {
        // If projectId is specified, force only 1 tab instance of MiniWEAT
        const { openTabs } = store.getState().mainTabs;
        const existAdrListTab = openTabs.find(
          (t) => t.projectId === projectId && t.docType === DOCTYPE.PROJECT_ASSIGNMENT
        );

        if (existAdrListTab)
          store.dispatch(mainTabActions.selectTab(existAdrListTab.tabId));
        else {
          store.dispatch(projActions.fetchJobStamp(projectId)).then((jobStamp) => {
            label = jobStamp.fullEA + ": " + label;
            const tab = {
              idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
              docType: DOCTYPE.PROJECT_ASSIGNMENT,
              label,
              projectId,
              createdOn: Date.now(),
            };
            store.dispatch(mainTabActions.addTab(tab));
          });
        }
      } else {
        const tab = {
          idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
          docType: DOCTYPE.PROJECT_ASSIGNMENT,
          label,
          projectId,
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_ADR_LIST: {
    run: (projectId) => {
      const { openTabs } = store.getState().mainTabs;

      // Force only 1 tab instance of ADR_LIST
      const existAdrListTab = openTabs.find(
        (t) => t.projectId === projectId && t.docType === DOCTYPE.ADR_LIST
      );

      if (existAdrListTab)
        store.dispatch(mainTabActions.selectTab(existAdrListTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.ADR_LIST,
          label: "Loading DR List...",
          projectId,
          createdOn: Date.now(),
        };
        const tabId = store.dispatch(mainTabActions.addTab(tab));
        store
          .dispatch(projActions.fetchJobStamp(projectId))
          .then((jobStamp) => {
            store.dispatch(
              mainTabActions.changeTabLabel(tabId, jobStamp.fullEA + ": DR List")
            );
          })
          .catch(() => store.dispatch(mainTabActions.closeTab(tabId)));
      }
    },
  },
  OPEN_RE_DASHBOARD: {
    run: (projectId, eaValue) => {
      const { openTabs } = store.getState().mainTabs; 

      // Check if eaValue is available 
      if (!eaValue) {
        console.warn("EA value not available for RE Dashboard");
        return;
      }
      const url = `https://tableau.dot.ca.gov/t/Construction/views/RE_ProjectDashboard_OngoingProjects_HQ_V1_0/ResidentEngineerProjectStatusDashboard?District-EA=${eaValue}`;
      window.open(url, '_blank');
    },
  },
  OPEN_PAY_ESTIMATES: {
    run: async (projectId) => {
      const { openTabs } = store.getState().mainTabs;

      const existPeTab = openTabs.find(
        (t) => t.projectId === projectId && t.docType === DOCTYPE.PROJECT_PAY_ESTIMATES
      );
      if (existPeTab) store.dispatch(mainTabActions.selectTab(existPeTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());

        // Initiate fetching of all static data (locationTypes, trades, etc.)
        store.dispatch(feederListActions.getAllStatic());
        const jobStamp = await store.dispatch(projActions.fetchJobStamp(projectId));

        // For loading project admin, validate genLocs first prior to
        // loading feederLists
        await store.dispatch(locationActions.validateGenericLocations(projectId));

        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.PROJECT_PAY_ESTIMATES,
          label: (jobStamp ? jobStamp.fullEA : "(EA N/A)") + ": Pay Estimates",
          projectId,
          createdOn: Date.now(),
          projectEA: (jobStamp ? jobStamp.fullEA : "(EA N/A)"),
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_QSHEET: {
    run: async (qSheet, estimateNumber, projectId, startDate, endDate, isClosed = false) => {
      const { openTabs } = store.getState().mainTabs;

      const existTab = openTabs.find(
        (t) =>
          t.docType === DOCTYPE.PROJECT_QSHEET &&
          t.docId === qSheet.qSheetId
      );
      if (existTab) {
        store.dispatch(mainTabActions.selectTab(existTab.tabId));
      } else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());

        store.dispatch(feederListActions.getAllStatic());
        const jobStamp = await store.dispatch(projActions.fetchJobStamp(projectId));

        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.PROJECT_QSHEET,
          label: (jobStamp ? jobStamp.fullEA : "(EA N/A)") + `: Est #${estimateNumber} – ${qSheet.itemNo}`,
          projectId,
          docId: qSheet.qSheetId,
          payEstimateId: qSheet.payEstimateId,
          estimateNumber,
          projectEA: jobStamp?.fullEA ?? null,
          itemNo: qSheet.itemNo,
          itemCode: qSheet.itemCode,
          description: qSheet.description,
          status: qSheet.statusLabel,
          startDate,
          endDate,
          isClosed,
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  OPEN_PAY_ESTIMATE_DETAIL: {
    run: async (estimate, projectId) => {
      const { openTabs } = store.getState().mainTabs;

      const existTab = openTabs.find(
        (t) =>
          t.docType === DOCTYPE.PROJECT_PAY_ESTIMATE_DETAIL &&
          t.docId === estimate.payEstimateId
      );
      if (existTab) {
        store.dispatch(mainTabActions.selectTab(existTab.tabId));
      } else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());

        store.dispatch(feederListActions.getAllStatic());
        const jobStamp = await store.dispatch(projActions.fetchJobStamp(projectId));

        const tab = {
          idCheckoutToken,
          docType: DOCTYPE.PROJECT_PAY_ESTIMATE_DETAIL,
          label: (jobStamp ? jobStamp.fullEA : "(EA N/A)") + `: Pay Estimate #${estimate.estimateNumber}`,
          projectId,
          docId: estimate.payEstimateId,
          estimateNumber: estimate.estimateNumber,
          isClosed: estimate.isClosed,
          payPeriod: {
            startDate: estimate.payPeriodStartDate,
            endDate: estimate.payPeriodEndDate,
          },
          createdOn: estimate.createdOn,
        };
        store.dispatch(mainTabActions.addTab(tab));
      }
    },
  },
  // Opens a Pay Estimate Detail tab from just a projectId + payEstimateId
  // (e.g. from a deep-linked URL), fetching the estimate itself rather than
  // requiring the caller to already have the row object in hand.
  OPEN_PAY_ESTIMATE_DETAIL_BY_ID: {
    run: async (projectId, payEstimateId) => {
      const { openTabs } = store.getState().mainTabs;

      const existTab = openTabs.find(
        (t) =>
          t.docType === DOCTYPE.PROJECT_PAY_ESTIMATE_DETAIL &&
          t.docId === payEstimateId
      );
      if (existTab) {
        store.dispatch(mainTabActions.selectTab(existTab.tabId));
        return;
      }

      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const tab = {
        idCheckoutToken,
        docType: DOCTYPE.PROJECT_PAY_ESTIMATE_DETAIL,
        label: "Loading Pay Estimate...",
        projectId,
        docId: payEstimateId,
        createdOn: Date.now(),
      };
      const tabId = store.dispatch(mainTabActions.addTab(tab));

      store.dispatch(feederListActions.getAllStatic());
      Promise.all([
        store.dispatch(projActions.fetchJobStamp(projectId)),
        store.dispatch(payEstimateActions.fetchDetail(payEstimateId, tabId)),
      ])
        .then(([jobStamp, estimate]) => {
          store.dispatch(
            mainTabActions.updateTab(tabId, {
              label: (jobStamp ? jobStamp.fullEA : "(EA N/A)") + `: Pay Estimate #${estimate.estimateNumber}`,
              estimateNumber: estimate.estimateNumber,
              isClosed: estimate.isClosed,
              payPeriod: {
                startDate: estimate.payPeriodStartDate,
                endDate: estimate.payPeriodEndDate,
              },
              createdOn: estimate.createdOn,
              projectEA: jobStamp ? jobStamp.fullEA : "(EA N/A)",
            })
          );
        })
        .catch(() => store.dispatch(mainTabActions.closeTab(tabId)));
    },
  },
  // Opens a QSheet tab from just a projectId + qSheetId (e.g. from a
  // deep-linked URL), fetching the QSheet itself rather than requiring the
  // caller to already have the row object and its parent estimate in hand.
  OPEN_QSHEET_BY_ID: {
    run: async (projectId, qSheetId) => {
      const { openTabs } = store.getState().mainTabs;

      const existTab = openTabs.find(
        (t) => t.docType === DOCTYPE.PROJECT_QSHEET && t.docId === qSheetId
      );
      if (existTab) {
        store.dispatch(mainTabActions.selectTab(existTab.tabId));
        return;
      }

      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const tab = {
        idCheckoutToken,
        docType: DOCTYPE.PROJECT_QSHEET,
        label: "Loading QSheet...",
        projectId,
        docId: qSheetId,
        createdOn: Date.now(),
      };
      const tabId = store.dispatch(mainTabActions.addTab(tab));

      store.dispatch(feederListActions.getAllStatic());
      store.dispatch(qSheetActions.fetchQSheet(qSheetId, tabId))
        .then((qSheet) => {
          store.dispatch(
            mainTabActions.updateTab(tabId, {
              label: (qSheet.projectEA ?? "(EA N/A)") + `: Est #${qSheet.estimateNumber} – ${qSheet.itemNo}`,
              payEstimateId: qSheet.payEstimateId,
              estimateNumber: qSheet.estimateNumber,
              projectEA: qSheet.projectEA ?? null,
              itemNo: qSheet.itemNo,
              description: qSheet.description,
              status: qSheet.statusLabel,
              startDate: qSheet.payPeriodStartDate,
              endDate: qSheet.payPeriodEndDate,
            })
          );
        })
        .catch(() => store.dispatch(mainTabActions.closeTab(tabId)));
    },
  },
  OPEN_PROJECT_DOCS: {
  run: (projectId, eaValue) => {
    const { openTabs } = store.getState().mainTabs; 

    // Check if eaValue is available 
    if (!eaValue) {
      console.warn("EA value not available for Project Docs");
      return;
    }
    const url = `https://ppmoe.dot.ca.gov/cc?id=cc_advertisement_details&ad_id=${eaValue}&active=false&focus=bidFiles&status=Approved`;
    window.open(url, '_blank');

  },
  },
  SEARCH_ADR: {
    // targetTabId is only provided for modifying an existing search. Leave as null
    // for new searches.
    run: async (searchParams, targetTabId = null) => {
      const jobStamp = store.getState().projects.jobStamps[searchParams.projectId];
      let idCheckoutToken = null;
      if (!targetTabId) {
        // Reserve a new tab if it's a new search
        idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      }

      try {
        await store.dispatch(
          adrActions.search(searchParams, idCheckoutToken, targetTabId)
        );
        // Modified search: results were written to the existing tab; just bring it forward.
        if (targetTabId) {
          store.dispatch(mainTabActions.selectTab(targetTabId));
          return;
        }
        // JobStamp is already available from when search modal was first loaded
        const tab = {
          idCheckoutToken,
          label: (jobStamp ? jobStamp.fullEA + ": " : "") + "DR Search Results",
          docType: DOCTYPE.ADR_SEARCH_RESULTS,
          projectId: searchParams.projectId,
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      } catch (e) {
        console.error(e);
        if (idCheckoutToken)
          store.dispatch(mainTabActions.unreserveTabId(idCheckoutToken));
      }
    },
  },
  CREATE_ADR: {
    run: async (projectId) => {
      // Add the Adr tab first before fetching created Adr
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.ADR,
        label: "Loading...",
        projectId,
        docId: 0,
        createdOn: Date.now(),
      };
      const tabId = store.dispatch(mainTabActions.addTab(tab));

      try {
        const [jobStamp] = await Promise.all([
          store.dispatch(projActions.fetchJobStamp(projectId)),
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);
        await store.dispatch(adrActions.create(tabId, projectId));

        const label = jobStamp.fullEA + ": New DR";
        store.dispatch(mainTabActions.updateTab(tabId, { label }));
      } catch (e) {
        console.error("error=", e);
        store.dispatch(mainTabActions.closeTab(tabId));
      }
    },
  },
  CREATE_ADR_V2: {
    run: async (authorUserId, projectId) => {
      // Add the Adr tab first before fetching created Adr
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.ADR,
        label: "Loading...",
        projectId,
        docId: 0,
        createdOn: Date.now(),
      };
      const tabId = store.dispatch(mainTabActions.addTab(tab));

      try {
        const [jobStamp] = await Promise.all([
          store.dispatch(projActions.fetchJobStamp(projectId)),
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);
        await store.dispatch(adrActions.createV2(tabId, authorUserId, projectId));

        const label = jobStamp.fullEA + ": New DR";
        store.dispatch(mainTabActions.updateTab(tabId, { label }));
      } catch (e) {
        console.error("error=", e);
        store.dispatch(mainTabActions.closeTab(tabId));
      }
    },
  },
  COPY_ADR: {
    run: async (tabIdOfOriginal, projectId, authorUserId) => {
      // Add a placeholder tab. Copying is a front-end only process - so
      // in most cases the placeholder tab will show and disappear fast.
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.ADR,
        label: "Loading...",
        projectId,
        docId: 0,
        createdOn: Date.now(),
      };
      // tabId of new tab that will hold the copied Adr
      const tabIdOfCopy = store.dispatch(mainTabActions.addTab(tab));

      const adr = store.getState().adr.openAdrs[tabIdOfOriginal];

      try {
        const [jobStamp] = await Promise.all([
          store.dispatch(projActions.fetchJobStamp(projectId)),
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);
        const copiedAdrId = await store.dispatch(
          adrActions.copy(tabIdOfCopy, adr.assistantDailyReportId, authorUserId)
        );
        const label = jobStamp.fullEA + ": New DR";

        store.dispatch(
          mainTabActions.updateTab(tabIdOfCopy, {
            label,
            docId: copiedAdrId,
          })
        );
      } catch (e) {
        console.error(e);
        store.dispatch(mainTabActions.closeTab(tabIdOfCopy));
      }
    },
  },
  FETCH_ADR: {
    run: async (projectId, adrId) => {
      const { openTabs } = store.getState().mainTabs;

      // Force only 1 tab instance of any given ADR (i.e. no multiple tabs
      // can be open of the same ADR)
      const existAdrTab = openTabs.find(
        (t) => t.projectId === projectId && t.docType === DOCTYPE.ADR && t.docId === adrId
      );

      if (existAdrTab) {
        store.dispatch(mainTabActions.selectTab(existAdrTab.tabId));
        return;
      }

      // Add the Adr tab first before we start fetching
      const tab = {
        idCheckoutToken: store.dispatch(mainTabActions.reserveTabId()),
        docType: DOCTYPE.ADR,
        label: "Loading...",
        projectId,
        createdOn: Date.now(),
      };
      const tabId = store.dispatch(mainTabActions.addTab(tab));

      try {
        const [jobStamp] = await Promise.all([
          store.dispatch(projActions.fetchJobStamp(projectId)),
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);

        // feederLists must be available prior to dispatching adrActions.fetch()
        const adrDto = await store.dispatch(adrActions.fetch(null, tabId, adrId));

        // Do not await when fetching amendment history. Prop update will update
        // the component.
        store.dispatch(adrActions.fetchAmendmentHistory(adrId));

        // TODO: label to say "Viewing" when !isAuthor
        const label = `${jobStamp.fullEA}: DR ${moment(adrDto.reportStartDate).format(
          "MM/DD/YYYY"
        )}`;
        const docId = adrId;
        store.dispatch(mainTabActions.updateTab(tabId, { label, docId }));
      } catch (err) {
        store.dispatch(mainTabActions.closeTab(tabId));
      }
    },
  },
  AMEND_ADR: {
    run: async (projectId, adrIdToAmend, amendedByUserId) => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      try {
        const [jobStamp] = await Promise.all([
          store.dispatch(projActions.fetchJobStamp(projectId)),
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);
        const docType = DOCTYPE.ADR;
        const label = jobStamp.fullEA + ": Editing DR (Amend.)";
        const newAmendmentId = await store.dispatch(
          adrActions.amend(idCheckoutToken, null, adrIdToAmend, amendedByUserId)
        );

        // newAmendmentId is null on error
        if (newAmendmentId)
          store.dispatch(adrActions.fetchAmendmentHistory(newAmendmentId));
        else return;

        const tab = {
          idCheckoutToken,
          label,
          docType,
          docId: newAmendmentId,
          projectId,
          createdOn: Date.now(),
        };
        store.dispatch(mainTabActions.addTab(tab));
      } catch (e) {
        store.dispatch(mainTabActions.unreserveTabId(idCheckoutToken));
      }
    },
  },
  REFETCH_ADR_IN_TAB: {
    // Called when "Keep editing" option is selected after saving ADR
    run: async (projectId, adrId, tabId) => {
      store.dispatch(mainTabActions.unmarkTabContentDirty(tabId));
      try {
        await Promise.all([
          store.dispatch(feederListActions.getAll(projectId)),
          store.dispatch(feederListActions.getAllStatic()),
        ]);
        await store.dispatch(adrActions.fetch(null, tabId, adrId));
        // Update the tab's docId in case it hasn't been set (i.e. when new drafts
        // are saved, then user chooses to keep editing.)
        store.dispatch(mainTabActions.changeMetadata(tabId, { docId: adrId }));
        store.dispatch(adrActions.fetchAmendmentHistory(adrId));
      } catch (e) {
        // Error alerting is assumed to have been taken care of by
        // the action creators. Since Adr form cannot function w/o
        // both getAll and fetchAdr completing successfully,
        // prevent user from proceeding with any further edits by
        // closing the tab.
        store.dispatch(mainTabActions.closeTab(tabId));
      }
    },
  },
  REFETCH_ADR_IN_TAB_AUTO: {
    run: async (adrId) => {
      const openAdrs = store.getState().adr.openAdrs || {};
      const tabId = Object.keys(openAdrs).find(
        (tabId) => openAdrs[tabId].assistantDailyReportId === adrId
      );

      console.log("tabId=", tabId);

      // If tab for this adr is not open, skip.
      if (!tabId) return;

      store.dispatch(mainTabActions.unmarkTabContentDirty(+tabId));

      await store.dispatch(adrActions.fetch(null, tabId, adrId));

      // Update the tab's docId in case it hasn't been set (i.e. when new drafts
      // are saved, then user chooses to keep editing.)
      store.dispatch(mainTabActions.changeMetadata(tabId, { docId: adrId }));
      store.dispatch(adrActions.fetchAmendmentHistory(adrId));
    },
  },
  UPDATE_ADR_STATUS: {
    run: (adrId, status) => {
      // Try to find tabId of the open adr
      const tabId = Object.keys(store.getState().adr.openAdrs).find(
        (tabId) => store.getState().adr.openAdrs[tabId].assistantDailyReportId === adrId
      );
      if (!tabId) return;

      store.dispatch({ type: ADR.UPDATE_STATUS, tabId, status });
      store.dispatch(adrActions.fetchAmendmentHistory(status.assistantDailyReportId));
    },
  },
  UPDATE_ADR_REVIEW_STARTED: {
    /**
     * Called in response to signalR message when another reviewer started/transferred
     * review of an ADR.
     * @param {Number} adrId
     * @param {Number} newReviewerUserId
     * @param {Date} newReviewStartedOn
     * @param {Boolean} revokeReview
     * @returns
     */
    run: (adrId, newReviewerUserId, newReviewStartedOn, revokeReview) => {
      // Try to find tabId of the open adr
      const tabId = Object.keys(store.getState().adr.openAdrs).find(
        (tabId) => store.getState().adr.openAdrs[tabId].assistantDailyReportId === adrId
      );
      if (!tabId) return;

      store.dispatch({
        type: ADR_REVIEW.START_FULFILLED,
        tabId: +tabId,
        reviewerUserId: newReviewerUserId,
        reviewStartedOn: newReviewStartedOn,
        revokeReview,
      });

      // Save review only if review is being revoked
      if (revokeReview) {
        store.dispatch(adrActions.saveReview(tabId));

        // If tab contents marked dirty, reset
        store.dispatch(mainTabActions.unmarkTabContentDirty(+tabId));
      }
    },
  },
  DELETE_ADR: {
    run: (projectId, adrId, tabId) => {
      store.dispatch(adrActions.deleteAdr(adrId, projectId)).then(() => {
        // tabId is optional, since delete can be triggered from outside the DR tab
        // (i.e. from AdrList). If tabId is provided, use it to close the tab.
        // If not, look for any open tabs with deleted adr, and close.
        if (tabId) store.dispatch(mainTabActions.closeTab(tabId));
        else {
          const { openTabs } = store.getState().mainTabs;
          const tabToClose = openTabs.find(
            (t) => t.docType === DOCTYPE.ADR && t.docId === adrId
          );
          if (tabToClose) store.dispatch(mainTabActions.closeTab(tabToClose.tabId));
        }
      });
    },
  },
  PRINT_ADR_ON_CLIENT: {
    run: async (tabId, previewOnly = false) => {
      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = store.getState().adr.openAdrs[tabId];
      if (!adr) return;

      // A report submitted before a since-merged equipment's merge still references it —
      // make sure that merged-away equipment is loaded so the PDF can still resolve its
      // Number/Description instead of showing "(No. N/A)".
      await store.dispatch(equipmentActions.getAllIncludingInactive(adr.projectId));

      const appSettings = store.getState().appSettings;
      const feederLists = getExistingFeederLists(adr.projectId);

      const wpSo = store.dispatch(adrActions.getWorkPerformedSortOrder(tabId));

      const sections = [
        {
          type: pdfDocSections.PDF_DOC_HEADER,
          renderSection: renderAdrHeaderV3(
            adrPdfSectionDataV3.headerData(adr, feederLists, appSettings.version)
          ), // Returns another function; "seeded" with data.
        },
        {
          type: pdfDocSections.PDF_DOC_FOOTER,
          renderSection: renderAdrFooterV3(),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrDescOfWorkV3(
            adrPdfSectionDataV3.descOfWork(adr, feederLists)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrWpHoursV3(
            adrPdfSectionDataV3.wpHours(adr, wpSo, feederLists)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrGenCommentsV3(
            adrPdfSectionDataV3.generalComments(adr.generalComments, feederLists)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_SIGNATURE,
          renderSection: renderAdrSignatureV3({ authorFullName: adr.authorFullName }),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrAttachmentsV3(),
        },
        {
          type: pdfDocSections.PDF_DOC_PAGENUMBER,
          renderSection: renderAdrPageNoV3(),
        },
      ];
      const adrPdf = new PdfDocument(sections);

      let attachments = [];

      // Add attachments
      Object.values(adr.workPerformedClient).forEach((wpc) => {
        wpc.locationWorkPerformed.forEach((locWp) => {
          locWp.attachments.forEach((locWpa) => {
            // Extract filename from the path if originalFileName doesn't exist
            const fileName = locWpa.originalFileName
              ? locWpa.originalFileName
              : locWpa.filePath.replace(/^.*[\\/]/, "");
            const ext = locWpa.originalFileName
              ? locWpa.originalFileName.split(".").pop().toLowerCase()
              : "jpg";
            if (fileExtensionIsImage(ext)) {
              const locId = locWp.isSublocation ? locWp.sublocationId : locWp.locationId;
              let locName = "";
              if (locWp.isSublocation) {
                switch (locWp.locationId) {
                  case -1:
                    break;
                  case -2: {
                    // Line-Sta
                    /** @type {SplitStationNumeric} */
                    const splitStationFromOrAt =
                      locWp.stationFromOrAt &&
                      convertNumberToSplitStation(
                        locWp.stationFromOrAt,
                        locWp.stationFromOrAtOffsetValue,
                        locWp.stationFromOrAtOffsetDirection
                      );
                    const splitStationTo =
                      locWp.stationTo &&
                      convertNumberToSplitStation(
                        locWp.stationTo,
                        locWp.stationToOffsetValue,
                        locWp.stationToOffsetDirection
                      );
                    const lineLabel = getLocationName(
                      locId,
                      feederLists.projLocationList
                    );
                    // Combine the location name string - e.g. "A1 Line 1234+12.34 100' Lt"
                    locName =
                      lineLabel +
                      " Line" +
                      (splitStationFromOrAt && splitStationTo ? ", from " : " ") +
                      (splitStationFromOrAt
                        ? convertSplitStationToString(splitStationFromOrAt)
                        : "") +
                      (splitStationTo
                        ? " to " + convertSplitStationToString(splitStationTo)
                        : "");
                    break;
                  }
                  default:
                    break;
                }
              } else {
                // Location is NOT a sublocation.
                locName = getLocationName(locId, feederLists.projLocationList);
              }

              attachments.push({
                imageUrl: `${
                  ["Local", "Test", "CT"].includes(appSettings.environmentContext)
                    ? "/files/"
                    : ""
                }${locWpa.imageThumbnailFilePath}`,
                context: {
                  uploadContext: contextTypes.LOCWP,
                  attachmentType: attachmentTypes[locWpa.attachmentType],
                  fileName,
                  locationName: locName,
                  wpCaption: getWpCaption(
                    wpc,
                    feederLists.projItemList,
                    feederLists.projCcoList,
                    feederLists.projPendingIssueList
                  ),
                  description: locWpa.description,
                  createdOn: locWpa.createdOn,
                },
              });

              adrPdf.addImage({
                imageUrl: `${
                  ["Local", "Test", "CT"].includes(appSettings.environmentContext)
                    ? "/files/"
                    : ""
                }${locWpa.imageThumbnailFilePath}`,
                context: {
                  uploadContext: contextTypes.LOCWP,
                  attachmentType: attachmentTypes[locWpa.attachmentType],
                  fileName,
                  locationName: locName,
                  wpCaption: getWpCaption(
                    wpc,
                    feederLists.projItemList,
                    feederLists.projCcoList,
                    feederLists.projPendingIssueList
                  ),
                  description: locWpa.description,
                  createdOn: locWpa.createdOn,
                },
              });
            }
          });
        });
      });

      adr.generalComments.forEach((gc) => {
        gc.commentAttachments.forEach((gca) => {
          const fileName = gca.originalFileName
            ? gca.originalFileName
            : gca.filePath.replace(/^.*[\\/]/, "");
          const ext = gca.originalFileName
            ? gca.originalFileName.split(".").pop().toLowerCase()
            : "jpg"; // This is for compatibility reasons
          if (!gca.isDeleted && fileExtensionIsImage(ext)) {
            attachments.push({
              imageUrl: `${
                ["Local", "Test", "CT"].includes(appSettings.environmentContext) ? "/files/" : ""
              }${gca.imageThumbnailFilePath}`,
              context: {
                uploadContext: contextTypes.GC,
                attachmentType: attachmentTypes[gca.attachmentType],
                fileName,
                description: gca.description,
                createdOn: gca.createdOn,
              },
            });

            adrPdf.addImage({
              imageUrl: `${
                ["Local", "Test", "CT"].includes(appSettings.environmentContext) ? "/files/" : ""
              }${gca.imageThumbnailFilePath}`,
              context: {
                uploadContext: contextTypes.GC,
                attachmentType: attachmentTypes[gca.attachmentType],
                fileName,
                description: gca.description,
                createdOn: gca.createdOn,
              },
            });
          }
        });
      });

      adrPdf.renderPdf(previewOnly);
    },
  },
  PRINT_TENTATIVE_AGREEMENT: {
    run: async (tabId, ta) => {
      const adr = store.getState().adr.openAdrs[tabId];
      if (!adr) return;

      // See PRINT_ADR_ON_CLIENT — merged-away equipment must still resolve for historical PDFs.
      await store.dispatch(equipmentActions.getAllIncludingInactive(adr.projectId));

      const version = store.getState().appSettings.version;
      const feederLists = getExistingFeederLists(adr.projectId);

      const sections = [
        {
          type: pdfDocSections.PDF_DOC_HEADER,
          renderSection: renderAdrTaHeaderV3(
            adrTaPdfSectionDataV3.headerData(adr, feederLists, ta, version)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrTaLaborV3(
            adrTaPdfSectionDataV3.laborData(adr, feederLists)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrTaEquipmentV3(
            adrTaPdfSectionDataV3.equipmentData(adr, feederLists)
          ),
        },
        {
          type: pdfDocSections.PDF_DOC_BODY,
          renderSection: renderAdrTaMaterialsV3(adrTaPdfSectionDataV3.materialsData(adr)),
        },
        {
          type: pdfDocSections.PDF_DOC_SIGNATURE,
          renderSection: renderAdrTaSignatureV3(adr),
        },
        {
          type: pdfDocSections.PDF_DOC_FOOTER,
          renderSection: renderAdrTaFooterV3(),
        },
      ];
      const adrTaPdf = new PdfDocument(sections);
      adrTaPdf.renderPdf(false);
    },
  },
  OPEN_ISSUE_TRACKER: {
    run: () => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      store.dispatch(issueActions.getAllIssues()).then(() => {
        const docType = DOCTYPE.ISSUE_TRACKER;
        const label = "Issue Tracker";
        store.dispatch(
          mainTabActions.addTab({
            idCheckoutToken,
            label,
            docType,
            createdOn: Date.now(),
          })
        );
      });
    },
  },
  OPEN_ISSUE: {
    run: (issueId) => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      store.dispatch(issueActions.getIssue(idCheckoutToken, issueId)).then(() => {
        const docType = DOCTYPE.ISSUE_THREAD;
        const label = `Issue #${issueId}: Thread`;
        store.dispatch(
          mainTabActions.addTab({
            idCheckoutToken,
            label,
            docType,
            docId: issueId,
            createdOn: Date.now(),
          })
        );
      });
    },
  },
  OPEN_ADMIN_TASKS: {
    run: () => {
      const { openTabs } = store.getState().mainTabs;

      const existAdminTasksTab = openTabs.find((t) => t.docType === DOCTYPE.ADMIN_TASKS);
      if (existAdminTasksTab !== undefined)
        store.dispatch(mainTabActions.selectTab(existAdminTasksTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
        const docType = DOCTYPE.ADMIN_TASKS;
        const label = "Admin Tasks";
        store.dispatch(
          mainTabActions.addTab({
            idCheckoutToken,
            label,
            docType,
            createdOn: Date.now(),
          })
        );
      }
    },
  },
  OPEN_QMS_DASH: {
    run: () => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const docType = DOCTYPE.QMS_DASHBOARD;
      const label = "QMS - Dashboard";
      store.dispatch(
        mainTabActions.addTab({
          idCheckoutToken,
          label,
          docType,
          createdOn: Date.now(),
        })
      );
    },
  },
  OPEN_QMS_ADMIN: {
    run: () => {
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const docType = DOCTYPE.QMS_ADMIN;
      const label = "QMS - Admin";
      store.dispatch(
        mainTabActions.addTab({
          idCheckoutToken,
          label,
          docType,
          createdOn: Date.now(),
        })
      );
    },
  },
  OPEN_QMS_CMT_LIST: {
    run: (divisionId) => {
      if (!divisionId) return;
      const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
      const docType = DOCTYPE.QMS_CHANGE_MANAGEMENT_TRACKER_LIST;
      const label = "CMT - List";
      store.dispatch(
        mainTabActions.addTab({
          idCheckoutToken,
          label,
          docType,
          createdOn: Date.now(),
          payload: {
            divisionId,
          },
        })
      );
    },
  },
  OPEN_QMS_CMT: {
    run: (divisionId, issueId, issueNo) => {
      const { openTabs } = store.getState().mainTabs;
      const existIssueTab = openTabs.find(
        (t) =>
          t.docType === DOCTYPE.QMS_CHANGE_MANAGEMENT_TRACKER &&
          t.payload &&
          "issueId" in t.payload &&
          t.payload.issueId === issueId
      );
      if (existIssueTab) store.dispatch(mainTabActions.selectTab(existIssueTab.tabId));
      else {
        const idCheckoutToken = store.dispatch(mainTabActions.reserveTabId());
        const docType = DOCTYPE.QMS_CHANGE_MANAGEMENT_TRACKER;
        const label = `CMT - Issue #${issueNo}`;
        store.dispatch(
          mainTabActions.addTab({
            idCheckoutToken,
            label,
            docType,
            createdOn: Date.now(),
            payload: {
              issueId,
              divisionId,
            },
          })
        );
      }
    },
  },
};

const getExistingFeederLists = (projectId) => {
  const jobStamp = store.getState().projects.jobStamps[projectId] || null;
  const projLocationList =
    store.getState().locations.projLocationLists[projectId] || null;
  const projContractorList =
    store.getState().contractors.projContractorLists[projectId] || null;
  const projItemList = store.getState().bidItems.projItemLists[projectId] || null;
  const projCcoList = store.getState().ccos.projCcoLists[projectId] || null;
  const projPendingIssueList =
    store.getState().pendingIssues.projPendingIssueLists[projectId] || null;
  const projLaborList = store.getState().labors.projLaborLists[projectId] || null;
  // Read-only PDF export: use the inactive-inclusive list so a report referencing
  // since-merged equipment still resolves its Number/Description (see PRINT_ADR_ON_CLIENT /
  // PRINT_TENTATIVE_AGREEMENT, which ensure this list is loaded before calling here). Do not
  // reuse this helper for anything that builds a selection UI.
  const projEquipList =
    store.getState().equipment.projEquipListsIncludingInactive[projectId] || null;
  const projKeywordList = store.getState().keywords.projKeywordLists[projectId] || null;
  const miscMaterialsUnitsOfMeasure =
    store.getState().feederLists.miscMaterialsUnitsOfMeasure || null;
  const fieldTestTypes = store.getState().feederLists.fieldTestTypes || null;
  const tradeList = store.getState().feederLists.tradeList || null;
  const locationTypeList = store.getState().feederLists.locationTypeList || null;

  const feederLists = {
    jobStamp,
    projItemList,
    projCcoList,
    projPendingIssueList,
    projLocationList,
    projContractorList,
    projLaborList,
    projEquipList,
    projKeywordList,
    miscMaterialsUnitsOfMeasure,
    fieldTestTypes,
    tradeList,
    locationTypeList,
  };

  return feederLists;
};
